import { confirm, isCancel } from "@clack/prompts";
import { openBrowser } from "../lib/open-browser";

/**
 * Response fields whose URL the user must visit in a browser to finish the
 * flow the command started: `authorize_url` (social account OAuth, e.g. Meta),
 * `session_url` (hosted KYC session, e.g. Sumsub), `deposit_url` (deposit
 * page returned on 402 payment-required errors), and `checkout_url` (a hosted
 * checkout the user must review and pay). `hosted_url` is deliberately excluded:
 * apps and deposits both return one, but for apps it's the app's canonical URL,
 * not a step the user must complete.
 */
export const ACTION_URL_FIELDS = [
	"authorize_url",
	"session_url",
	"deposit_url",
	"checkout_url",
] as const;

export function findActionUrl(payload: unknown): string | null {
	if (
		payload == null ||
		typeof payload !== "object" ||
		Array.isArray(payload)
	) {
		return null;
	}
	const rec = payload as Record<string, unknown>;
	for (const field of ACTION_URL_FIELDS) {
		const value = rec[field];
		if (typeof value === "string" && /^https?:\/\//i.test(value)) return value;
	}
	return null;
}

export type ActionUrlOutcome = "opened" | "manual" | "agent";

export function actionUrlNotice(
	url: string,
	outcome: ActionUrlOutcome,
): string {
	switch (outcome) {
		case "opened":
			return `Opened your browser to complete this step:\n${url}\nIf it didn't open, visit the link above.`;
		case "manual":
			return `Complete this step in your browser:\n${url}`;
		case "agent":
			return `Action required: open this link in the user's browser to complete this step:\n${url}`;
	}
}

async function confirmOpenInBrowser(url: string): Promise<boolean> {
	const answer = await confirm({
		message: `This step needs to be completed in your browser. Open ${url} now?`,
	});
	return !isCancel(answer) && answer === true;
}

export async function announceActionUrl(
	url: string,
	{
		isTTY = process.stdout.isTTY === true && process.stdin.isTTY === true,
		confirmOpen = confirmOpenInBrowser,
		open = openBrowser,
		print = (text) => process.stderr.write(`${text}\n`),
	}: {
		isTTY?: boolean;
		confirmOpen?: (url: string) => Promise<boolean>;
		open?: (url: string) => void;
		print?: (text: string) => void;
	} = {},
): Promise<void> {
	if (!isTTY) {
		print(actionUrlNotice(url, "agent"));
		return;
	}
	const shouldOpen = await confirmOpen(url);
	if (shouldOpen) open(url);
	print(actionUrlNotice(url, shouldOpen ? "opened" : "manual"));
}
